import React, { useEffect } from "react";
import { Switch, FormControlLabel, Divider } from "@material-ui/core";
import { CTextField, CSelect, HFlex, Text, MultiSelect } from "shared";
import { Controller, useFormContext } from "react-hook-form";
import { useHcpProfessionals } from "api/hooks/useHcpServices";
import { ProfessionalItem, ServiceItem } from "routes/hcp/types";

export interface ServiceFormData {
  health_care_professional_ids: number[];
  name: string;
  description: string;
  duration: string;
  mode: string;
  purpose: string;
  payment_methods: string[];
  opening_hours: string;
  closing_hours: string;
  no_earlier_than: string;
  no_later_than: string;
  capacity: string;
  health_questionnaire_type: string;
  bookable_by_patients: boolean;
}

const HOUR = 60;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;

// Bookable workday range and granularity, matching the provider portal's service form
const WORKDAY_START = 6 * HOUR; // 06:00
const WORKDAY_END = 22 * HOUR; // 22:00
const WORKDAY_STEP = 15;

const CAPACITY_MIN = 1;
const CAPACITY_MAX = 100;

interface Option {
  value: string;
  label: string;
}

const durationOptions = [
  { value: "15", label: "15 min" },
  { value: "20", label: "20 min" },
  { value: "30", label: "30 min" },
  { value: "45", label: "45 min" },
];

const modeOptions = [
  { value: "physical", label: "Physical" },
  { value: "digital", label: "Digital" },
];

const purposeOptions = [
  { value: "kick_off", label: "Kick Off" },
  { value: "follow_up", label: "Follow Up" },
  { value: "general", label: "General" },
  { value: "discharge_call", label: "Discharge Call" },
];

const paymentMethodOptions = [
  { value: "bank_card", label: "Bank Card" },
  { value: "on_site", label: "On Site" },
  { value: "exemption_card", label: "Exemption Card" },
  { value: "free", label: "Free" },
];

const questionnaireOptions = [
  { value: "default", label: "Default" },
  { value: "none", label: "None" },
];

const pluralize = (count: number, unit: string) => `${count} ${count === 1 ? unit : `${unit}s`}`;

const formatTime = (minutes: number) =>
  `${String(Math.floor(minutes / HOUR)).padStart(2, "0")}:${String(minutes % HOUR).padStart(2, "0")}`;

const formatDuration = (minutes: number) => {
  if (minutes % WEEK === 0) return pluralize(minutes / WEEK, "week");
  if (minutes % DAY === 0) return pluralize(minutes / DAY, "day");
  if (minutes % HOUR === 0) return pluralize(minutes / HOUR, "hour");
  return pluralize(minutes, "minute");
};

const workdayOptions: Option[] = Array.from(
  { length: Math.floor((WORKDAY_END - WORKDAY_START) / WORKDAY_STEP) + 1 },
  (_, index) => {
    const minutes = WORKDAY_START + index * WORKDAY_STEP;
    return { value: String(minutes), label: formatTime(minutes) };
  }
);

const noEarlierThanOptions: Option[] = [1, 2, 3, 4].map(hours => ({
  value: String(hours * HOUR),
  label: pluralize(hours, "hour"),
}));

const noLaterThanOptions: Option[] = [3, 7, 14, 28].map(days => ({
  value: String(days * DAY),
  label: pluralize(days, "day"),
}));

export const serviceFormDefaults = {
  opening_hours: String(7 * HOUR),
  closing_hours: String(22 * HOUR),
  no_earlier_than: String(2 * HOUR),
  no_later_than: String(28 * DAY),
};

// Keeps a stored value selectable when it predates these option sets
const withCurrentValue = (options: Option[], value: string, formatLabel: (minutes: number) => string): Option[] => {
  const minutes = Number(value);
  if (!value || isNaN(minutes) || options.some(option => option.value === value)) return options;
  return [...options, { value, label: formatLabel(minutes) }].sort((a, b) => Number(a.value) - Number(b.value));
};

export const serviceToFormValues = (service: ServiceItem): ServiceFormData => ({
  name: service.name,
  description: service.description ?? "",
  duration: String(service.duration),
  mode: service.mode,
  purpose: service.purpose,
  payment_methods: service.payment_methods ?? [],
  opening_hours: service.opening_hours != null ? String(service.opening_hours) : serviceFormDefaults.opening_hours,
  closing_hours: service.closing_hours != null ? String(service.closing_hours) : serviceFormDefaults.closing_hours,
  no_earlier_than: service.no_earlier_than != null ? String(service.no_earlier_than) : "",
  no_later_than: service.no_later_than != null ? String(service.no_later_than) : "",
  capacity: service.capacity != null ? String(service.capacity) : "",
  health_questionnaire_type: service.health_questionnaire_type ?? "none",
  bookable_by_patients: service.bookable_by_patients,
  health_care_professional_ids: service.health_care_professional_ids ?? [],
});

export const formValuesToPayload = (fields: ServiceFormData) => ({
  name: fields.name,
  description: fields.description,
  duration: Number(fields.duration),
  mode: fields.mode,
  purpose: fields.purpose,
  payment_methods: fields.payment_methods.length > 0 ? fields.payment_methods : undefined,
  opening_hours: fields.opening_hours ? Number(fields.opening_hours) : undefined,
  closing_hours: fields.closing_hours ? Number(fields.closing_hours) : undefined,
  no_earlier_than: fields.no_earlier_than ? Number(fields.no_earlier_than) : undefined,
  no_later_than: fields.no_later_than ? Number(fields.no_later_than) : undefined,
  capacity: fields.capacity ? Number(fields.capacity) : undefined,
  health_questionnaire_type: fields.health_questionnaire_type || undefined,
  bookable_by_patients: fields.bookable_by_patients,
  health_care_professional_ids: fields.health_care_professional_ids,
});

const professionalLabel = (professional: ProfessionalItem) =>
  professional.name ||
  [professional.first_name, professional.last_name].filter(Boolean).join(" ") ||
  professional.email ||
  `Professional #${professional.id}`;

interface Props {
  hcpId: number;
  paymentMethodsDefault: string[];
  professionalIdsDefault?: number[];
}

const ServiceFormFields: React.VFC<Props> = ({ hcpId, paymentMethodsDefault, professionalIdsDefault = [] }) => {
  const { control, watch, setValue } = useFormContext<ServiceFormData>();

  const { data: professionals, isLoading: isLoadingProfessionals } = useHcpProfessionals(hcpId);
  const professionalOptions = (professionals ?? []).map(p => ({ value: p.id, label: professionalLabel(p) }));

  const openingHours = watch("opening_hours");
  const closingHours = watch("closing_hours");
  const noEarlierThan = watch("no_earlier_than");
  const noLaterThan = watch("no_later_than");

  // Closing time cannot precede opening time
  useEffect(() => {
    if (openingHours && closingHours && Number(closingHours) < Number(openingHours)) {
      setValue("closing_hours", openingHours);
    }
  }, [openingHours, closingHours, setValue]);

  const closingOptions = withCurrentValue(workdayOptions, closingHours, formatTime).filter(
    option => !openingHours || Number(option.value) >= Number(openingHours)
  );

  return (
    <>
      <CTextField required name="name" autoFocus label="Name" margin="normal" size="small" fullWidth />
      <CTextField required name="description" label="Description" margin="normal" size="small" fullWidth />

      <HFlex gap={2}>
        <CSelect name="duration" label="Duration" items={durationOptions} margin="normal" size="small" fullWidth>
          {({ label }) => <Text>{label}</Text>}
        </CSelect>
        <CSelect name="mode" label="Mode" items={modeOptions} margin="normal" size="small" fullWidth>
          {({ label }) => <Text>{label}</Text>}
        </CSelect>
        <CSelect name="purpose" label="Purpose" items={purposeOptions} margin="normal" size="small" fullWidth>
          {({ label }) => <Text>{label}</Text>}
        </CSelect>
      </HFlex>

      <Divider style={{ margin: "16px 0 8px" }} />

      <Text bold mb={1}>
        Scheduling
      </Text>

      <MultiSelect
        name="payment_methods"
        label="Payment Methods"
        options={paymentMethodOptions}
        defaultValue={paymentMethodsDefault}
        margin="normal"
      />

      <HFlex gap={2}>
        <CSelect
          name="opening_hours"
          label="Opening Hours"
          items={withCurrentValue(workdayOptions, openingHours, formatTime)}
          margin="normal"
          size="small"
          fullWidth
        >
          {({ label }) => <Text>{label}</Text>}
        </CSelect>
        <CSelect
          name="closing_hours"
          label="Closing Hours"
          items={closingOptions}
          margin="normal"
          size="small"
          fullWidth
        >
          {({ label }) => <Text>{label}</Text>}
        </CSelect>
      </HFlex>

      <HFlex gap={2}>
        <CSelect
          name="no_earlier_than"
          label="No Earlier Than"
          items={withCurrentValue(noEarlierThanOptions, noEarlierThan, formatDuration)}
          margin="normal"
          size="small"
          fullWidth
        >
          {({ label }) => <Text>{label}</Text>}
        </CSelect>
        <CSelect
          name="no_later_than"
          label="No Later Than"
          items={withCurrentValue(noLaterThanOptions, noLaterThan, formatDuration)}
          margin="normal"
          size="small"
          fullWidth
        >
          {({ label }) => <Text>{label}</Text>}
        </CSelect>
      </HFlex>

      <HFlex gap={2}>
        <CTextField
          name="capacity"
          label="Capacity"
          type="number"
          asNumber
          inputProps={{ min: CAPACITY_MIN, max: CAPACITY_MAX }}
          margin="normal"
          size="small"
          fullWidth
        />
        <CSelect
          name="health_questionnaire_type"
          label="Health Questionnaire"
          items={questionnaireOptions}
          margin="normal"
          size="small"
          fullWidth
        >
          {({ label }) => <Text>{label}</Text>}
        </CSelect>
      </HFlex>

      <Controller
        name="bookable_by_patients"
        control={control}
        render={({ value, onChange }) => (
          <FormControlLabel
            control={<Switch checked={value} onChange={e => onChange(e.target.checked)} color="primary" />}
            label="Bookable by Patients"
          />
        )}
      />

      <Divider style={{ margin: "16px 0 8px" }} />

      <Text bold mb={1}>
        Professionals
      </Text>

      <MultiSelect
        name="health_care_professional_ids"
        label="Professionals"
        options={professionalOptions}
        defaultValue={professionalIdsDefault}
        loading={isLoadingProfessionals}
        noOptionsText="No professionals found"
        margin="normal"
      />
    </>
  );
};

export default ServiceFormFields;
